import React, { Component, RefObject } from 'react';

import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Tooltip from '@atlaskit/tooltip';

import { Link } from '@qality/common-components/src/atoms/Link';
import DeleteTestCycleDialog from '@qality/common-components/src/test-cycle/DeleteTestCycleDialog';
import { FEATURE_FLAG } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { TestCycle, TestCycleProject } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestCycleHelper,
  TestCycleService,
} from '@qality/common-service';

import EditableTitle from '../EditableTitle';
import './_test-cycle-header.scss';
import './_test-cycle-list-tab.scss';

type Props = {
  testCycle: TestCycle;
  canEdit: boolean;
  saveTestCycleName: (value: string) => Promise<void>;
};

type State = {
  name: string;
  isNameInvalid: boolean;
  isOpen: boolean;
  project: TestCycleProject;
  testCyclesUrl: string;
};

export default class TestCycleHeader extends Component<Props, State> {
  oldName: string;
  editableTitleRef: RefObject<HTMLDivElement>;
  constructor(props: Props) {
    super(props);
    this.state = {
      name: props.testCycle.name,
      isNameInvalid: false,
      isOpen: false,
      project: props.testCycle.project,
      testCyclesUrl: '',
    };
    this.oldName = props.testCycle.name;
    this.editableTitleRef = React.createRef();
  }

  async componentDidMount(): Promise<void> {
    await this.getTestCyclesUrl();
  }

  getTestCyclesUrl = async (): Promise<void> => {
    const testCyclesUrl = await StorageOfUniqueServices.get(
      'URLService'
    ).getTestCyclesUrl(this.state.project.key, this.state.project.id);
    this.setState({ testCyclesUrl });
  };

  openDialog = (): void => this.setState({ isOpen: true });

  closeDialog = (): void => this.setState({ isOpen: false });

  deleteCycle = (): Promise<void> =>
    TestCycleHelper.deleteTestCycle(this.props.testCycle, true);

  getActions(): Array<JSX.Element> {
    const actions = [];
    if (this.props.canEdit) {
      actions.push(
        <DropdownItem
          key='clone'
          onClick={() =>
            TestCycleService.cloneTestCycle(this.props.testCycle.id)
          }
        >
          <div className='qality-test-cycle-detail-actions-wrapper'>
            <div className='qality-actions-icon'>
              <CopyIcon label='CopyIcon' />
            </div>
            <div
              className='qality-actions-label'
              id='qality-test-cycle-header-clone'
            >
              {messages['testCycle.details.header.action.clone']}
            </div>
          </div>
        </DropdownItem>
      );
      actions.push(
        <DropdownItem key='delete' onClick={this.openDialog}>
          <div className='qality-test-cycle-detail-actions-wrapper'>
            <div className='qality-actions-icon'>
              <EditorRemoveIcon label='RemoveIcon' />
            </div>
            <div
              className='qality-actions-label'
              id='qality-test-cycle-header-delete'
            >
              {messages['testCycle.details.header.action.delete']}
            </div>
          </div>
        </DropdownItem>
      );
    }
    return actions;
  }

  render(): JSX.Element {
    const items = this.getActions();
    return (
      <div className='test-cycle-header-container'>
        <div className='test-cycle-header'>
          <p className='test-cycle-header-name text-wrapper'>
            <Link
              onClick={() => {
                StorageOfUniqueServices.get('AP').navigator.go('site', {
                  absoluteUrl: this.state.testCyclesUrl,
                });
              }}
              gray
            >
              Test Cycles
            </Link>{' '}
            / {this.props.testCycle.name}
          </p>
          <div className='test-cycle-header-menu'>
            <Tooltip
              content={
                !FEATURE_FLAG.isPremium
                  ? 'Test Execution Report of Test Cycle available in QAlity Plus'
                  : ''
              }
            >
              <Button
                iconBefore={<GraphBarIcon label='' />}
                onClick={() =>
                  TestCycleService.navigateToTestExecutionReportOfCycle(
                    this.props.testCycle.id,
                    this.state.project
                  )
                }
                isDisabled={!FEATURE_FLAG.isPremium}
              >
                Test Execution Report
              </Button>
            </Tooltip>
            <DropdownMenu
              shouldFlip={false}
              placement='left-start'
              trigger={({ triggerRef, ...props }) => (
                <Button
                  {...props}
                  iconBefore={
                    <MoreIcon
                      size='small'
                      primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                      label='MoreIcon'
                    />
                  }
                  className='text-cycle-header-actions-button'
                  ref={triggerRef}
                  appearance='default'
                />
              )}
            >
              <DropdownItemGroup>{items}</DropdownItemGroup>
            </DropdownMenu>
          </div>
        </div>
        <EditableTitle
          value={this.state.name}
          onChange={this.handleNameChange}
          onConfirm={this.handleNameChangeConfirm}
          onCancel={this.handleNameCancel}
          canEdit={this.props.canEdit}
          isInvalid={this.state.isNameInvalid}
          innerRef={this.editableTitleRef}
        />
        <DeleteTestCycleDialog
          isOpen={this.state.isOpen}
          onClose={this.closeDialog}
          onDelete={this.deleteCycle}
          testCycleName={this.props.testCycle.name}
        />
      </div>
    );
  }

  handleNameChange = (name: string, invalid: boolean): void => {
    this.setState({
      name: name,
      isNameInvalid: invalid,
    });
  };

  handleNameChangeConfirm = (value: string): boolean => {
    this.setState({ name: value }, () => {
      if (this.state.name !== '') {
        this.props.saveTestCycleName(value);
        this.oldName = this.state.name;
      } else {
        this.setState({ name: this.oldName });
      }
    });
    return true;
  };

  handleNameCancel = (): void => {
    this.setState({
      name: this.oldName,
      isNameInvalid: false,
    });
  };
}
